import * as React from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  cn,
} from "@nexus/ui"
import { useNavigate } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
  connectionsQueryOptions,
  orderHandoffDefaultsQueryOptions,
  personaOpportunitySetDetailQueryOptions,
  useCreateOrder,
  useRetryOrder,
  vendorsQueryOptions,
} from "@/lib/queries"
import {
  createOrderErrorMessage,
  createOrderTimeoutErrorMessage,
  isRecoverableOrderCreateError,
  readCreateOrderUnavailable,
} from "@/features/marketplace/lib/order-errors"
import { pollSentScriptsConfirmation } from "@/features/marketplace/lib/poll-sent-scripts-confirmation"
import { findVendorSend, itemIdsNotWithVendor } from "@/features/marketplace/lib/sent-scripts-map"
import {
  applyHandoffEdits,
  planPartnerSends,
  togglePartnerSelection,
  type PartnerSend,
} from "@/features/marketplace/lib/partner-send-plan"
import { useMultiPartnerSend } from "@/features/marketplace/components/use-multi-partner-send"
import {
  usePartnerReviewDrafts,
  type ReviewDraft,
} from "@/features/marketplace/components/use-partner-review-drafts"
import { SendPartnerResultsView } from "@/features/marketplace/components/SendPartnerResultsView"
import {
  emptyHandoffDefaultsFormState,
  handoffDefaultsFromApi,
} from "@/features/marketplace/components/MarketplaceOrderHandoffDefaultsForm"
import { SendOrderHandoffStep } from "@/features/marketplace/components/SendOrderHandoffStep"
import { TargetRatioOrderSummary } from "@/features/marketplace/components/TargetRatiosPicker"
import { useTargetRatioSelection } from "@/features/marketplace/components/use-target-ratio-selection"
import { SendVendorCloseConfirm } from "@/features/marketplace/components/SendVendorCloseConfirm"
import {
  parseReviewRecipients,
  SendOrderReviewStep,
} from "@/features/marketplace/components/SendOrderReviewStep"
import type { MarketplaceOrderHandoffDefaults } from "@nexus/contracts"
import {
  useIamPermission,
  useIsActiveSuperTenant,
  usePermissions,
} from "@/features/auth/hooks/usePermissions"
import { connectionNeedsResign } from "@/features/marketplace/lib/terms-compliance"
import { ConnectWizard } from "@/features/marketplace/components/ConnectWizard/ConnectWizard"
import { ReSignSheet } from "@/features/marketplace/components/ReSignSheet"
import { useConnectWizardStore } from "@/features/marketplace/store/connectWizard.store"
import { usePendingSendStore } from "@/features/marketplace/store/pendingSend.store"
import type {
  MarketplaceOrderStatus,
  SentScriptsMap,
  TenantVendorConnection,
  Vendor,
} from "@/features/marketplace/types/marketplace.types"
import type { PersonaScriptHookDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api"
import { PersonaSendHookVariationSet } from "./PersonaSendHookVariationSet"
import { SendSuccessView } from "./PersonaSendSuccessView"
import { SendAlreadySentView } from "./PersonaSendAlreadySentView"
import { SendPendingRetryView } from "@/features/opportunities/components/scripts/SendToVendorDialog/SendPendingRetryView"
import {
  SendVendorOptionCard,
  SendVendorOptionCardSkeleton,
  type SendVendorOptionStatus,
} from "./PersonaSendVendorOptionCard"

const SUCCESS_AUTO_CLOSE_MS = 5000

type SendDialogStep = "select-vendor" | "handoff-details" | "review"

interface SuccessInfo {
  vendorName: string
  count: number
  orderId?: string
}

interface PendingRetryInfo {
  vendorName: string
  vendorId: string | undefined
  /** Items on the order being retried, fixed at submit so a refreshed sent map cannot change the batch. */
  itemIds: string[]
  orderId: string
  status: MarketplaceOrderStatus
}

interface VendorOption {
  vendor: Vendor
  connection: TenantVendorConnection | null
  status: SendVendorOptionStatus
}

export interface PersonaSendToVendorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** All script IDs the user intends to send (may include already-sent). */
  scriptIds?: string[]
  /** All brief IDs the user intends to send (may include already-sent). */
  briefIds?: string[]
  scriptTitlesById?: Readonly<Record<string, string>>
  briefTitlesById?: Readonly<Record<string, string>>
  /** Hooks per script at the latest version; scripts with more than one show as a variation set. */
  scriptHooksById?: Readonly<Record<string, readonly PersonaScriptHookDto[]>>
  sentScriptsMap: SentScriptsMap
  tenantId: string
  opportunitySetId: string
}

function resolveVendorStatus(
  vendor: Vendor,
  connection: TenantVendorConnection | null,
): SendVendorOptionStatus {
  if (!connection || connection.disconnectedAt !== null) return "not-connected"
  if (!vendor.isActive) return "inactive"
  if (connectionNeedsResign(connection)) return "needs-resign"
  return "connected"
}

function buildVendorOptions(
  vendors: Vendor[],
  connections: TenantVendorConnection[],
  vendorHasEveryItem: (vendorId: string) => boolean,
): VendorOption[] {
  const connectionByVendorId = new Map(
    connections
      .filter((c) => c.disconnectedAt === null)
      .map((c) => [c.vendorId, c] as const),
  )

  // Keep inactive vendors in the list so resolveVendorStatus can mark them
  // "inactive" (the card renders a disabled badge) and a pending intent that
  // targets a now-inactive vendor isn't silently dropped.
  return vendors.map((vendor) => {
    const connection = connectionByVendorId.get(vendor.id) ?? null
    return {
      vendor,
      connection,
      status: vendorHasEveryItem(vendor.id)
        ? "already-sent"
        : resolveVendorStatus(vendor, connection),
    }
  })
}

export function PersonaSendToVendorDialog({
  open,
  onOpenChange,
  scriptIds = [],
  briefIds = [],
  scriptTitlesById = {},
  briefTitlesById = {},
  scriptHooksById = {},
  sentScriptsMap,
  tenantId,
  opportunitySetId,
}: PersonaSendToVendorDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const canReviewHandoffEmail = useIsActiveSuperTenant()
  const permissions = usePermissions()
  const canViewMarketplace = useIamPermission(permissions.MARKETPLACE_VIEW_PAGE)
  const [selectedVendorIds, setSelectedVendorIds] = React.useState<string[]>([])
  const selectedVendorId = selectedVendorIds[0] ?? ""
  const setSelectedVendorId = (vendorId: string) => setSelectedVendorIds(vendorId ? [vendorId] : [])
  const [reviewIndex, setReviewIndex] = React.useState(0)
  const [resignOpen, setResignOpen] = React.useState(false)
  const [connectOpen, setConnectOpen] = React.useState(false)
  const [successInfo, setSuccessInfo] = React.useState<SuccessInfo | null>(null)
  const [pendingRetry, setPendingRetry] = React.useState<PendingRetryInfo | null>(null)
  const [sendError, setSendError] = React.useState<string | null>(null)
  const [handoffOverrideNote, setHandoffOverrideNote] = React.useState("")
  const [handoffDefaults, setHandoffDefaults] = React.useState<MarketplaceOrderHandoffDefaults>(
    emptyHandoffDefaultsFormState(),
  )
  const [handoffDefaultsBaseline, setHandoffDefaultsBaseline] =
    React.useState<MarketplaceOrderHandoffDefaults>(emptyHandoffDefaultsFormState())
  const [recipientsTo, setRecipientsTo] = React.useState("")
  const [recipientsCc, setRecipientsCc] = React.useState("")
  const [handoffSubject, setHandoffSubject] = React.useState("")
  const [reviewCanSend, setReviewCanSend] = React.useState(true)
  const [step, setStep] = React.useState<SendDialogStep>("select-vendor")
  const [handoffConnectionId, setHandoffConnectionId] = React.useState("")
  const [isAwaitingSend, setIsAwaitingSend] = React.useState(false)
  const [closeConfirmOpen, setCloseConfirmOpen] = React.useState(false)
  const successHandledRef = React.useRef(false)
  const pollAbortRef = React.useRef<AbortController | null>(null)
  const nestedDialogOpenRef = React.useRef(false)
  // Guards the connection-id → vendor-id hydration so a connections refetch
  // can't clobber a selection the user (or the open-reset) already made.
  const pendingHydratedRef = React.useRef(false)

  React.useEffect(() => {
    return () => {
      pollAbortRef.current?.abort()
    }
  }, [])

  const pendingIntent = usePendingSendStore((s) => s.intent)
  const setPendingIntent = usePendingSendStore((s) => s.setIntent)
  const clearPendingIntent = usePendingSendStore((s) => s.clearIntent)
  const openWizard = useConnectWizardStore((s) => s.openWizard)
  const closeWizard = useConnectWizardStore((s) => s.closeWizard)

  const setDetailQuery = useQuery(
    personaOpportunitySetDetailQueryOptions(
      open ? tenantId : null,
      open ? opportunitySetId : null,
    ),
  )
  const platformsReady =
    !setDetailQuery.isLoading &&
    (setDetailQuery.isError || setDetailQuery.data !== undefined)
  const ratioSelection = useTargetRatioSelection({
    open,
    adPlatforms: setDetailQuery.data?.set.config?.adPlatforms ?? null,
    platformsReady,
  })
  const vendorsQuery = useQuery(vendorsQueryOptions(tenantId))
  const connectionsQuery = useQuery(connectionsQueryOptions(tenantId))
  const connections = connectionsQuery.data ?? []
  const allItemIds = [...scriptIds, ...briefIds]
  const vendorOptions = buildVendorOptions(
    vendorsQuery.data ?? [],
    connections,
    (vendorId) =>
      allItemIds.length > 0 &&
      itemIdsNotWithVendor(sentScriptsMap, allItemIds, vendorId).length === 0,
  )

  const createOrder = useCreateOrder()
  const retryOrder = useRetryOrder()
  const queryClient = useQueryClient()
  const multiSend = useMultiPartnerSend({ tenantId, buildPayload: buildPartnerPayload })
  const isSubmitting =
    createOrder.isPending || isAwaitingSend || retryOrder.isPending || multiSend.isSending

  const selectedOption = vendorOptions.find((o) => o.vendor.id === selectedVendorId)
  const isMultiPartner = selectedVendorIds.length > 1
  const partnerSends = isMultiPartner
    ? planPartnerSends(
        sentScriptsMap,
        selectedVendorIds.flatMap((id) => {
          const option = vendorOptions.find((o) => o.vendor.id === id)
          // A partner can disconnect or need re-signing after it was ticked; only send to ready ones.
          return option?.connection && option.status === "connected"
            ? [{ connectionId: option.connection.id, vendorId: id, vendorName: option.vendor.name }]
            : []
        }),
        scriptIds,
        briefIds,
      )
    : []
  const reviewPartner = isMultiPartner ? partnerSends[reviewIndex] : undefined
  const isLastReviewPartner = reviewIndex >= partnerSends.length - 1
  // One send per item per partner: only items the chosen partner does not have yet go out.
  const unsentScriptIds = isMultiPartner
    ? scriptIds.filter((id) => partnerSends.some((s) => s.scriptIds.includes(id)))
    : selectedOption
      ? itemIdsNotWithVendor(sentScriptsMap, scriptIds, selectedOption.vendor.id)
      : scriptIds
  const unsentBriefIds = isMultiPartner
    ? briefIds.filter((id) => partnerSends.some((s) => s.briefIds.includes(id)))
    : selectedOption
      ? itemIdsNotWithVendor(sentScriptsMap, briefIds, selectedOption.vendor.id)
      : briefIds
  const alreadySentIds = allItemIds.filter((id) =>
    selectedVendorIds.some((vendorId) => findVendorSend(sentScriptsMap, id, vendorId) !== null),
  )
  const alreadySentItems = alreadySentIds.flatMap((id) => {
    const title = scriptTitlesById[id] ?? briefTitlesById[id]
    return title ? [{ id, title }] : []
  })
  const unsentHookVariations = unsentScriptIds.map((id) => ({
    scriptId: id,
    title: scriptTitlesById[id] ?? "",
    hooks: scriptHooksById[id] ?? [],
  }))
  const unsentIds = [...unsentScriptIds, ...unsentBriefIds]
  const allAlreadySent =
    vendorOptions.length > 0 && vendorOptions.every((o) => o.status === "already-sent")

  const selectedConnection = selectedOption?.connection ?? null
  const selectedNeedsResign = selectedOption?.status === "needs-resign"
  const selectedNotConnected = selectedOption?.status === "not-connected"
  const selectedCanSend = selectedOption?.status === "connected"
  const selectedAlreadySent = selectedOption?.status === "already-sent"
  const sendsOnPrimaryAction =
    (step === "review" && (!isMultiPartner || isLastReviewPartner)) ||
    (!canReviewHandoffEmail && step === "handoff-details")
  const reviewDrafts = usePartnerReviewDrafts(
    step === "select-vendor" ? null : tenantId,
    partnerSends,
  )
  const reviewDraft = reviewPartner
    ? reviewDrafts.draftFor(reviewPartner.connectionId)
    : { to: recipientsTo, cc: recipientsCc, subject: handoffSubject, canSend: reviewCanSend }

  // The review preview must show the email this partner will actually get: its own
  // package values for every field the user left alone.
  const reviewPartnerDefaults = reviewPartner ? reviewDrafts.defaultsFor(reviewPartner.connectionId) : null
  const reviewHandoffDefaults = reviewPartnerDefaults
    ? applyHandoffEdits(reviewPartnerDefaults, handoffDefaults, handoffDefaultsBaseline)
    : handoffDefaults
  const reviewBaseline = reviewPartnerDefaults ?? handoffDefaultsBaseline

  function updateReviewDraft(patch: Partial<ReviewDraft>) {
    if (reviewPartner) reviewDrafts.updateDraft(reviewPartner.connectionId, patch)
  }

  async function buildPartnerPayload(send: PartnerSend) {
    const preview = await queryClient.fetchQuery(
      orderHandoffDefaultsQueryOptions(tenantId, send.connectionId),
    )
    const partnerDefaults = handoffDefaultsFromApi("defaults" in preview ? preview.defaults : preview)
    const draft = reviewDrafts.draftFor(send.connectionId)
    return {
      tenantId,
      tenantVendorId: send.connectionId,
      scriptIds: send.scriptIds,
      briefIds: send.briefIds,
      handoffDefaultsOverride: applyHandoffEdits(
        partnerDefaults,
        handoffDefaults,
        handoffDefaultsBaseline,
      ),
      targetRatios: ratioSelection.selected,
      ...(handoffOverrideNote.trim()
        ? { handoffOverrideNote: handoffOverrideNote.trim() }
        : {}),
      ...(canReviewHandoffEmail
        ? {
            recipientEmails: parseReviewRecipients(draft.to),
            ccEmails: parseReviewRecipients(draft.cc),
            handoffSubject: draft.subject.trim() || undefined,
          }
        : {}),
    }
  }

  const handoffDefaultsQuery = useQuery(
    orderHandoffDefaultsQueryOptions(
      step === "handoff-details" ? tenantId : null,
      step === "handoff-details" ? handoffConnectionId : null,
    ),
  )

  const isLoading = vendorsQuery.isLoading || connectionsQuery.isLoading
  const loadFailed = vendorsQuery.isError || connectionsQuery.isError

  const wasOpenRef = React.useRef(false)

  React.useEffect(() => {
    const opening = open && !wasOpenRef.current
    wasOpenRef.current = open

    if (!open) return

    if (!opening) return

    setSuccessInfo(null)
    setPendingRetry(null)
    setSendError(null)
    setHandoffOverrideNote("")
    setHandoffDefaults(emptyHandoffDefaultsFormState())
    setHandoffDefaultsBaseline(emptyHandoffDefaultsFormState())
    setRecipientsTo("")
    setRecipientsCc("")
    setHandoffSubject("")
    setReviewCanSend(true)
    setStep("select-vendor")
    setHandoffConnectionId("")
    setIsAwaitingSend(false)
    setReviewIndex(0)
    reviewDrafts.resetDrafts()
    multiSend.reset()
    pollAbortRef.current?.abort()
    successHandledRef.current = false
    pendingHydratedRef.current = false
    createOrder.reset()

    const sameTenant = pendingIntent?.tenantId === tenantId
    if (sameTenant && pendingIntent?.vendorId && !pendingIntent.tenantVendorId) {
      setSelectedVendorId(pendingIntent.vendorId)
      pendingHydratedRef.current = true
    } else if (!(sameTenant && pendingIntent?.tenantVendorId)) {
      // No connection-id to resolve — selection is final here.
      setSelectedVendorId("")
      pendingHydratedRef.current = true
    }
    // The tenantVendorId case needs `connections`, which may not have loaded
    // yet; the hydration effect below resolves it once they arrive.
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, pendingIntent?.vendorId]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open || pendingHydratedRef.current) return
    if (pendingIntent?.tenantId !== tenantId || !pendingIntent.tenantVendorId) return
    const conn = connections.find((c) => c.id === pendingIntent.tenantVendorId)
    if (conn) {
      setSelectedVendorId(conn.vendorId)
      pendingHydratedRef.current = true
    }
  }, [open, tenantId, pendingIntent?.tenantId, pendingIntent?.tenantVendorId, connections])

  React.useEffect(() => {
    if (vendorOptions.length === 0) return
    // A refresh can mark a ticked partner as already sent or no longer ready. Its card is
    // then disabled and cannot be unticked, so drop it here.
    const statusOf = (id: string) => vendorOptions.find((o) => o.vendor.id === id)?.status
    const isMulti = selectedVendorIds.length > 1
    const kept = selectedVendorIds.filter((id) => {
      const status = statusOf(id)
      if (!status || status === "already-sent") return false
      return !isMulti || status === "connected"
    })
    if (kept.length !== selectedVendorIds.length) setSelectedVendorIds(kept)
  }, [selectedVendorIds, vendorOptions])

  React.useEffect(() => {
    if (handoffDefaultsQuery.data) {
      const preview = handoffDefaultsQuery.data
      const defaults = "defaults" in preview ? preview.defaults : preview
      const recipients = "recipients" in preview ? preview.recipients : { to: [], cc: [] }
      const parsed = handoffDefaultsFromApi(defaults)
      setHandoffDefaults(parsed)
      setHandoffDefaultsBaseline(parsed)
      setRecipientsTo(recipients.to.join(", "))
      setRecipientsCc(recipients.cc.join(", "))
    }
  }, [handoffDefaultsQuery.data])

  function submitOrder(
    connectionId: string,
    nextScriptIds: string[],
    nextBriefIds: string[],
  ) {
    setSendError(null)
    pollAbortRef.current?.abort()
    const pollAbort = new AbortController()
    pollAbortRef.current = pollAbort
    const itemIds = [...nextScriptIds, ...nextBriefIds]
    const itemCount = itemIds.length
    const vendorId = connections.find((c) => c.id === connectionId)?.vendorId

    createOrder.mutate(
      {
        tenantId,
        tenantVendorId: connectionId,
        scriptIds: nextScriptIds,
        briefIds: nextBriefIds,
        handoffDefaultsOverride: handoffDefaults,
        targetRatios: ratioSelection.selected,
        ...(handoffOverrideNote.trim()
          ? { handoffOverrideNote: handoffOverrideNote.trim() }
          : {}),
        ...(canReviewHandoffEmail
          ? {
              recipientEmails: parseReviewRecipients(recipientsTo),
              ccEmails: parseReviewRecipients(recipientsCc),
              handoffSubject: handoffSubject.trim() || undefined,
            }
          : {}),
      },
      {
        onSuccess: (result) => {
          const conn = connections.find((c) => c.id === connectionId)
          const vendorName = conn?.vendor.name ?? t("personaOpportunities.scripts.sendDialog.vendorLabel")
          successHandledRef.current = false
          setSuccessInfo({
            vendorName,
            count: itemCount,
            orderId: result.orderId ?? result.data?.id ?? undefined,
          })
        },
        onError: async (err) => {
          if (isRecoverableOrderCreateError(err)) {
            const conn = connections.find((c) => c.id === connectionId)
            const vendorName =
              conn?.vendor.name ?? t("personaOpportunities.scripts.sendDialog.vendorLabel")
            const seeded = readCreateOrderUnavailable(err)
            if (seeded) {
              setPendingRetry({
                vendorName,
                vendorId,
                itemIds,
                orderId: seeded.orderId,
                status: seeded.status,
              })
            }
            setIsAwaitingSend(true)
            const polled = await pollSentScriptsConfirmation(
              queryClient,
              tenantId,
              itemIds,
              pollAbort.signal,
              vendorId,
            )
            if (pollAbort.signal.aborted) return
            setIsAwaitingSend(false)
            if (polled.outcome === "sent") {
              successHandledRef.current = false
              setSuccessInfo({ vendorName, count: itemCount, orderId: polled.orderId })
              setPendingRetry(null)
              return
            }
            if (polled.outcome === "pending") {
              setPendingRetry({
                vendorName,
                vendorId,
                itemIds,
                orderId: polled.orderId,
                status: polled.status,
              })
              return
            }
            if (seeded) return
            setSendError(createOrderTimeoutErrorMessage(t))
            return
          }
          setSendError(createOrderErrorMessage(err, t))
        },
      },
    )
  }

  function handleSuccessDone() {
    if (successHandledRef.current || !successInfo) {
      onOpenChange(false)
      return
    }
    successHandledRef.current = true
    toast.success(
      successInfo.count === 1
        ? t("personaOpportunities.scripts.sendDialog.successToast_one", { vendorName: successInfo.vendorName })
        : t("personaOpportunities.scripts.sendDialog.successToast_other", {
            count: successInfo.count,
            vendorName: successInfo.vendorName,
          }),
    )
    clearPendingIntent()
    onOpenChange(false)
  }

  function viewOrderHref(orderId: string): string {
    if (canViewMarketplace) {
      return `/marketplace?tab=in-production&order=${encodeURIComponent(orderId)}`
    }
    const search = new URLSearchParams({
      setId: opportunitySetId,
      tab: "sent_to_vendor",
      orderId,
    })
    return `/persona-opportunities?${search.toString()}`
  }

  function navigateToOrder(orderId: string) {
    if (canViewMarketplace) {
      void navigate({
        to: "/marketplace",
        search: { tab: "in-production", order: orderId },
      })
      return
    }
    void navigate({
      to: "/persona-opportunities",
      search: (previous) => ({
        ...previous,
        setId: opportunitySetId,
        tab: "sent_to_vendor",
        orderId,
      }),
    })
  }

  function handleSend() {
    if (!selectedOption || unsentIds.length === 0) return

    if (
      (step === "review" || step === "handoff-details") &&
      ratioSelection.selected.length === 0
    ) {
      return
    }

    if (isMultiPartner) {
      handleMultiPartnerStep()
      return
    }

    if (step === "review") {
      if (!handoffConnectionId) return
      submitOrder(handoffConnectionId, unsentScriptIds, unsentBriefIds)
      return
    }

    if (step === "handoff-details") {
      if (!handoffConnectionId) return
      if (canReviewHandoffEmail) {
        setStep("review")
        return
      }
      submitOrder(handoffConnectionId, unsentScriptIds, unsentBriefIds)
      return
    }

    if (selectedNotConnected) {
      nestedDialogOpenRef.current = true
      setPendingIntent({ tenantId, scriptIds: unsentScriptIds, briefIds: unsentBriefIds, vendorId: selectedOption.vendor.id })
      openWizard(selectedOption.vendor.id)
      setConnectOpen(true)
      return
    }

    if (!selectedConnection) return

    if (selectedNeedsResign) {
      nestedDialogOpenRef.current = true
      setPendingIntent({
        tenantId,
        tenantVendorId: selectedConnection.id,
        scriptIds: unsentScriptIds,
        briefIds: unsentBriefIds,
      })
      setResignOpen(true)
      return
    }

    if (selectedCanSend && selectedConnection) {
      setHandoffConnectionId(selectedConnection.id)
      setStep("handoff-details")
    }
  }

  function handleMultiPartnerStep() {
    const first = partnerSends[0]
    if (!first) return
    if (step === "select-vendor") {
      setHandoffConnectionId(first.connectionId)
      setStep("handoff-details")
      return
    }
    if (step === "handoff-details" && canReviewHandoffEmail) {
      setReviewIndex(0)
      setStep("review")
      return
    }
    if (step === "review" && !isLastReviewPartner) {
      setReviewIndex(reviewIndex + 1)
      return
    }
    void multiSend.sendAll(partnerSends)
  }

  function handleBack() {
    if (step === "review" && isMultiPartner && reviewIndex > 0) {
      setReviewIndex(reviewIndex - 1)
      return
    }
    setStep(step === "review" ? "handoff-details" : "select-vendor")
  }

  function handlePartnerResultsDone() {
    if (multiSend.results?.every((r) => r.outcome === "sent")) {
      toast.success(
        t("marketplace.sendHandoff.partnerResults.successToast", {
          count: multiSend.results.length,
        }),
      )
      clearPendingIntent()
    }
    onOpenChange(false)
  }

  function handleConnectSuccess(connection: TenantVendorConnection) {
    nestedDialogOpenRef.current = false
    setConnectOpen(false)
    closeWizard()
    clearPendingIntent()
    setSelectedVendorId(connection.vendorId)
    setHandoffConnectionId(connection.id)
    setStep("handoff-details")
  }

  function handleConnectClose() {
    nestedDialogOpenRef.current = false
    setConnectOpen(false)
    closeWizard()
  }

  function handleResignSuccess() {
    const intent = usePendingSendStore.getState().intent
    if (!intent?.tenantVendorId || intent.tenantId !== tenantId) return
    nestedDialogOpenRef.current = false
    setResignOpen(false)
    setHandoffConnectionId(intent.tenantVendorId)
    setStep("handoff-details")
  }

  function handleResignClose() {
    nestedDialogOpenRef.current = false
    setResignOpen(false)
  }

  function handleDialogClose(nextOpen: boolean) {
    if (nextOpen) return
    // Closing unmounts the dialog and would stop the partners not yet sent.
    if (multiSend.isSending) return
    if (multiSend.results) {
      handlePartnerResultsDone()
      return
    }
    if (successInfo) {
      handleSuccessDone()
      return
    }
    if (pendingRetry) {
      onOpenChange(false)
      return
    }
    if (createOrder.isPending || isAwaitingSend) return
    if (!resignOpen && !connectOpen) clearPendingIntent()
    onOpenChange(false)
  }

  function handleRetryLoad() {
    void vendorsQuery.refetch()
    void connectionsQuery.refetch()
  }

  React.useEffect(() => {
    if (!open) {
      nestedDialogOpenRef.current = false
      setResignOpen(false)
      setConnectOpen(false)
      setCloseConfirmOpen(false)
    }
  }, [open])

  const dialogHidden = resignOpen || connectOpen
  const isResultView = Boolean(multiSend.results || successInfo || pendingRetry || allAlreadySent)

  return (
    <>
      <Dialog open={open && !dialogHidden} onOpenChange={handleDialogClose}>
        <DialogContent
          variant={isResultView ? "confirmation" : "form"}
          className={cn(
            "flex w-full flex-col overflow-hidden",
            !isResultView &&
              step === "review" &&
              "sm:max-w-[min(1100px,calc(100vw-2rem))]",
            !isResultView && step === "review"
              ? "h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)]"
              : "max-h-[min(92vh,960px)]",
          )}
          onInteractOutside={(event) => {
            event.preventDefault()
            if (
              nestedDialogOpenRef.current ||
              multiSend.results ||
              successInfo ||
              pendingRetry ||
              isSubmitting
            ) {
              return
            }
            setCloseConfirmOpen(true)
          }}
        >
          {multiSend.results ? (
            <SendPartnerResultsView
              results={multiSend.results}
              retrying={multiSend.isSending}
              onRetryFailed={multiSend.retryFailed}
              onViewOrder={(orderId) => {
                navigateToOrder(orderId)
                onOpenChange(false)
              }}
              onDone={handlePartnerResultsDone}
            />
          ) : pendingRetry ? (
            <SendPendingRetryView
              vendorName={pendingRetry.vendorName}
              status={pendingRetry.status}
              retrying={retryOrder.isPending}
              onRetry={() => {
                pollAbortRef.current?.abort()
                const pollAbort = new AbortController()
                pollAbortRef.current = pollAbort
                retryOrder.mutate(
                  { orderId: pendingRetry.orderId, tenantId },
                  {
                    onSuccess: async () => {
                      setIsAwaitingSend(true)
                      const polled = await pollSentScriptsConfirmation(
                        queryClient,
                        tenantId,
                        pendingRetry.itemIds,
                        pollAbort.signal,
                        pendingRetry.vendorId,
                      )
                      if (pollAbort.signal.aborted) return
                      setIsAwaitingSend(false)
                      if (polled.outcome === "sent") {
                        successHandledRef.current = false
                        setPendingRetry(null)
                        setSuccessInfo({
                          vendorName: pendingRetry.vendorName,
                          count: pendingRetry.itemIds.length,
                          orderId: polled.orderId,
                        })
                        return
                      }
                      if (polled.outcome === "pending") {
                        setPendingRetry({
                          ...pendingRetry,
                          orderId: polled.orderId,
                          status: polled.status,
                        })
                      }
                    },
                  },
                )
              }}
              onViewOrder={() => {
                navigateToOrder(pendingRetry.orderId)
                onOpenChange(false)
              }}
              onClose={() => onOpenChange(false)}
            />
          ) : successInfo ? (
            <SendSuccessView
              vendorName={successInfo.vendorName}
              count={successInfo.count}
              autoCloseMs={SUCCESS_AUTO_CLOSE_MS}
              onDone={handleSuccessDone}
              orderId={successInfo.orderId}
              viewOrderHref={
                successInfo.orderId ? viewOrderHref(successInfo.orderId) : undefined
              }
              onViewOrder={() => {
                if (!successInfo.orderId) return
                navigateToOrder(successInfo.orderId)
              }}
              onSendAnother={() => {
                successHandledRef.current = true
                setSuccessInfo(null)
                setHandoffSubject("")
                setReviewCanSend(true)
                setStep("select-vendor")
                setHandoffConnectionId("")
                setSelectedVendorId("")
              }}
            />
          ) : allAlreadySent ? (
            <SendAlreadySentView
              scriptIds={[...scriptIds, ...briefIds]}
              sentScriptsMap={sentScriptsMap}
              onClose={() => onOpenChange(false)}
            />
          ) : (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="text-base">
                  {t("personaOpportunities.scripts.sendDialog.title", "Send to production partner")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    `personaOpportunities.scripts.sendDialog.description_${unsentIds.length === 1 ? "one" : "other"}`,
                    { count: unsentIds.length },
                  )}
                </DialogDescription>
              </DialogHeader>

              <div
                className={cn(
                  "min-h-0 flex-1 px-1 -mx-1",
                  step === "review" ? "flex flex-col overflow-hidden" : "overflow-y-auto",
                )}
              >
              {loadFailed ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="alert-circle" size="sm" className="mx-auto mb-2 text-destructive/60" />
                  <p className="text-sm font-medium text-foreground">
                    {vendorsQuery.isError
                      ? t("personaOpportunities.scripts.sendDialog.vendorsFailed")
                      : t("personaOpportunities.scripts.sendDialog.connectionsFailed")}
                  </p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={handleRetryLoad}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : isLoading ? (
                <div className="flex flex-col gap-2">
                  <SendVendorOptionCardSkeleton />
                  <SendVendorOptionCardSkeleton />
                </div>
              ) : vendorOptions.length === 0 ? (
                <div className="rounded-lg border border-border bg-muted/30 px-4 py-5 text-center">
                  <Icon name="building-2" size="sm" className="mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-sm font-medium text-foreground">
                    {t("personaOpportunities.scripts.sendDialog.noVendors")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("personaOpportunities.scripts.sendDialog.noVendorsHint")}
                  </p>
                </div>
              ) : (
                <div className={cn("flex flex-col gap-3", step === "review" && "min-h-0 flex-1 overflow-hidden")}>
                  {sendError && !isSubmitting && (
                    <div
                      role="alert"
                      className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-destructive" />
                      <p className="text-xs text-foreground/80">{sendError}</p>
                    </div>
                  )}

                  {isSubmitting && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
                    >
                      <Icon
                        name="loader"
                        size="xs"
                        className="mt-0.5 shrink-0 animate-spin text-muted-foreground"
                      />
                      <p className="text-xs text-foreground/80">
                        {t("personaOpportunities.scripts.sendDialog.confirming")}
                      </p>
                    </div>
                  )}

                  {alreadySentIds.length > 0 && !allAlreadySent && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <div className="min-w-0 text-xs text-foreground/80">
                        <p>
                          {t(
                            isMultiPartner
                              ? `marketplace.sendHandoff.alreadyWithSomePartners_${alreadySentIds.length === 1 ? "one" : "other"}`
                              : alreadySentIds.length === 1
                                ? "personaOpportunities.scripts.sendDialog.alreadySentWarning_one"
                                : "personaOpportunities.scripts.sendDialog.alreadySentWarning_other",
                            { count: alreadySentIds.length },
                          )}
                        </p>
                        {alreadySentItems.length > 0 ? (
                          <ul className="mt-1 list-disc space-y-0.5 pl-4 font-medium text-foreground">
                            {alreadySentItems.map((script) => (
                              <li key={script.id} className="break-words">
                                {script.title}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  )}

                  {selectedNeedsResign && (
                    <div
                      role="status"
                      className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2.5"
                    >
                      <Icon name="alert-circle" size="xs" className="mt-0.5 shrink-0 text-warning" />
                      <p className="text-xs text-foreground/80">
                        {t("marketplace.resign.sendBlocked")}
                      </p>
                    </div>
                  )}

                  {!allAlreadySent && step === "handoff-details" ? (
                    <SendOrderHandoffStep
                      handoffOverrideNote={handoffOverrideNote}
                      onHandoffOverrideNoteChange={setHandoffOverrideNote}
                      handoffDefaults={handoffDefaults}
                      onHandoffDefaultsChange={setHandoffDefaults}
                      handoffDefaultsBaseline={handoffDefaultsBaseline}
                      platformFamily={ratioSelection.family}
                      targetRatios={ratioSelection.selected}
                      onToggleTargetRatio={ratioSelection.toggle}
                      ratiosReady={ratioSelection.isReady}
                      showEmptyError={ratioSelection.showEmptyError}
                      disabled={isSubmitting}
                      isLoadingDefaults={handoffDefaultsQuery.isLoading}
                      defaultsFailed={handoffDefaultsQuery.isError}
                      onRetryDefaults={() => void handoffDefaultsQuery.refetch()}
                    />
                  ) : null}

                  {!allAlreadySent && step === "review" && reviewPartner ? (
                    <p className="text-xs font-medium text-muted-foreground">
                      {t("marketplace.sendHandoff.reviewPartnerStep", {
                        index: reviewIndex + 1,
                        count: partnerSends.length,
                        vendor: reviewPartner.vendorName,
                      })}
                    </p>
                  ) : null}

                  {!allAlreadySent && step === "review" ? (
                    <SendOrderReviewStep
                      key={reviewPartner?.connectionId ?? "single"}
                      tenantId={tenantId}
                      tenantVendorId={
                        reviewPartner?.connectionId ||
                        handoffConnectionId ||
                        selectedOption?.connection?.id ||
                        ""
                      }
                      scriptIds={reviewPartner?.scriptIds ?? unsentScriptIds}
                      briefIds={reviewPartner?.briefIds ?? unsentBriefIds}
                      vendorName={reviewPartner?.vendorName ?? selectedOption?.vendor.name ?? ""}
                      handoffOverrideNote={handoffOverrideNote}
                      handoffDefaults={reviewHandoffDefaults}
                      targetRatios={ratioSelection.selected}
                      baseline={reviewBaseline}
                      recipientsTo={reviewDraft.to}
                      recipientsCc={reviewDraft.cc}
                      subject={reviewDraft.subject}
                      onRecipientsToChange={reviewPartner ? (to) => updateReviewDraft({ to }) : setRecipientsTo}
                      onRecipientsCcChange={reviewPartner ? (cc) => updateReviewDraft({ cc }) : setRecipientsCc}
                      onSubjectChange={
                        reviewPartner ? (subject) => updateReviewDraft({ subject }) : setHandoffSubject
                      }
                      onCanSendChange={
                        reviewPartner ? (canSend) => updateReviewDraft({ canSend }) : setReviewCanSend
                      }
                      onEditSource={() => setStep("handoff-details")}
                      disabled={isSubmitting}
                    />
                  ) : null}

                  {!allAlreadySent && step === "select-vendor" && (
                    <PersonaSendHookVariationSet scripts={unsentHookVariations} />
                  )}

                  {!allAlreadySent && step === "select-vendor" && (
                    <p className="text-xs text-muted-foreground">
                      {t("marketplace.sendHandoff.selectPartnersHint")}
                    </p>
                  )}

                  {!allAlreadySent && step === "select-vendor" && (
                    <div
                      className="flex flex-col gap-2"
                      role="group"
                      aria-label={t("personaOpportunities.scripts.sendDialog.vendorLabel")}
                    >
                      {vendorOptions.map((option) => (
                        <SendVendorOptionCard
                          key={option.vendor.id}
                          vendorName={option.vendor.name}
                          logoUrl={option.connection?.vendor.logoUrl ?? option.vendor.logoUrl}
                          description={option.vendor.description}
                          selected={selectedVendorIds.includes(option.vendor.id)}
                          status={option.status}
                          onSelect={() =>
                            setSelectedVendorIds((ids) =>
                              togglePartnerSelection(
                                ids,
                                option.vendor.id,
                                (id) => vendorOptions.find((o) => o.vendor.id === id)?.status === "connected",
                              ),
                            )
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              </div>

              <DialogFooter className="shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {!allAlreadySent && (step === "handoff-details" || step === "review") ? (
                  <TargetRatioOrderSummary
                    scriptCount={unsentIds.length}
                    ratioCount={ratioSelection.selected.length}
                  />
                ) : (
                  <span className="hidden sm:block" />
                )}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                {loadFailed || vendorOptions.length === 0 ? (
                  <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onOpenChange(false)}
                      disabled={isSubmitting}
                    >
                      {t("common.cancel")}
                    </Button>
                    {!allAlreadySent && (step === "handoff-details" || step === "review") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleBack}
                        disabled={isSubmitting}
                      >
                        {t("common.back")}
                      </Button>
                    )}
                    {!allAlreadySent && selectedVendorId && (
                      <Button
                        size="sm"
                        onClick={handleSend}
                        disabled={
                          isSubmitting ||
                          (step === "handoff-details" &&
                            (handoffDefaultsQuery.isLoading ||
                              handoffDefaultsQuery.isError ||
                              !ratioSelection.isReady ||
                              ratioSelection.selected.length === 0)) ||
                          (step === "review" && ratioSelection.selected.length === 0) ||
                          (step === "review" &&
                            (parseReviewRecipients(reviewDraft.to).length === 0 || !reviewDraft.canSend)) ||
                          (step === "select-vendor" &&
                            (selectedOption?.status === "inactive" ||
                              selectedAlreadySent ||
                              (!selectedCanSend && !selectedNotConnected && !selectedNeedsResign)))
                        }
                        iconStart={
                          isSubmitting
                            ? "loader"
                            : sendsOnPrimaryAction
                              ? "upload"
                              : selectedNeedsResign
                                ? "file-text"
                                : selectedNotConnected
                                  ? "link"
                                  : "arrow-right"
                        }
                        className={isSubmitting ? "[&_svg]:animate-spin" : ""}
                      >
                        {isSubmitting
                          ? t("personaOpportunities.scripts.sendDialog.confirming")
                          : sendsOnPrimaryAction && isMultiPartner
                            ? t("marketplace.sendHandoff.sendToPartners", { count: partnerSends.length })
                          : sendsOnPrimaryAction
                            ? t("marketplace.sendHandoff.sendToVendor", {
                                vendor: selectedOption?.vendor.name ?? "",
                                defaultValue: "Send to {{vendor}}",
                              })
                            : step === "review" && isMultiPartner
                              ? t("marketplace.sendHandoff.nextPartner")
                            : step === "handoff-details"
                              ? t("marketplace.sendHandoff.reviewContinue", "Review email")
                            : selectedNeedsResign
                              ? t("marketplace.resign.action")
                              : selectedNotConnected
                                ? t("personaOpportunities.scripts.sendDialog.connectAndSend")
                                : t("personaOpportunities.scripts.sendDialog.continue")}
                      </Button>
                    )}
                  </>
                )}
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <SendVendorCloseConfirm
        open={closeConfirmOpen}
        onStay={() => setCloseConfirmOpen(false)}
        onClose={() => {
          setCloseConfirmOpen(false)
          handleDialogClose(false)
        }}
      />

      {selectedOption && connectOpen && (
        <ConnectWizard
          vendorId={selectedOption.vendor.id}
          tenantId={tenantId}
          open={connectOpen}
          onClose={handleConnectClose}
          onConnectSuccess={handleConnectSuccess}
        />
      )}

      {selectedConnection && (
        <ReSignSheet
          connectionId={selectedConnection.id}
          vendorId={selectedConnection.vendorId}
          tenantId={tenantId}
          open={resignOpen}
          onClose={handleResignClose}
          onSuccess={handleResignSuccess}
        />
      )}
    </>
  )
}
